import React, {
	useMemo,
	useEffect,
	useRef,
	useState,
	useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Expand } from '@sparefoot/react-core';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import { TrackingClient } from '@sparefoot/tracking-client';
import {
	scrollTo,
	scrollToElement,
} from '@sparefoot/react-core/utils/scrolling';
import { change as changeFormData } from 'redux-form';

import { metaPropTypes, phonePropType } from 'config/propTypes';
import facilitySelector from 'store/selectors/facility';
import { getFacilityCardPhoneNumber } from 'store/modules/facilities';
import { createBooking } from 'store/modules/bookings';
import { onCreateReview } from 'store/modules/reviews';
import usePageViewTracking from 'hooks/usePageViewTracking';
import useSearchHandler from 'hooks/useSearchHandler';
import useActiveSection from 'hooks/useActiveSection';
import useViewportWidth from 'hooks/useViewportWidth';
import { isLarge } from 'utils/breakpoints';
import { GTM_IDS } from 'utils/gtmSelectors';
import ErrorPage from 'pages/ErrorPage/ErrorPage';
import { LoadingPage } from 'components/core/LoadingPage';
import OGFFacilityPage from 'pages/OGFFacilityPage/OGFFacilityPage';
import { H2 } from 'components/core/Typography';
import { Page, Section, Container } from 'components/core/Page';
import {
	FacilityBreadcrumbs,
	FacilityGallery,
	HowItWorks,
	TopBanner,
	StorageNearYou,
} from 'components/organisms';
import FacilityReviews from 'components/organisms/FacilityPage/FacilityReviews/FacilityReviews';
import { ReviewForm } from 'components/review/ReviewForm';
import { ReviewTabs, GoogleReviewCard } from 'components/molecules';
import { SelfStorageSchema } from 'components/facility/SelfStorageSchema';
import { WebPageSchema } from 'components/facility/WebPageSchema';
import { WebSiteSchema } from 'components/facility/WebSiteSchema';
import { UnitProductSchema } from 'components/facility/UnitProductSchema';
import { FacilityDetails } from 'components/organisms/FacilityPage/FacilityDetails/FacilityDetails';
import FacilityAmenitiesRanked from 'components/facility/FacilityAmenitiesRanked/FacilityAmenitiesRanked';
import { FacilityUnitListV2 } from 'components/facility/FacilityUnitListV2/FacilityUnitListV2';
import { FeaturesAmenitiesAccordion } from 'components/facility/FacilityAmenitiesAccordion/FacilityAmenitiesAccordion';
import { StickyReserveBar } from 'components/facility/StickyReserveBar/StickyReserveBar';
import { trackCTAClick } from 'components/core/library/tracking-utils/tracking/clickTrackingUtils';
import OMIIcon from 'assets/images/icons/omi-Icon.svg';
import Coupon from 'assets/images/icons/coupon.svg';
import questionMarkIcon from 'assets/images/icons/icon-question-mark.svg';

import './FacilityPageV2.scss';

// SF-22689: sections the mobile sticky jump nav scrolls between. Declared
// as a module-level constant so its identity is stable across renders -
// useActiveSection tears down and recreates its observer whenever this
// array's reference changes.
const STICKY_NAV_SECTIONS = [
	{ id: 'overview', label: 'Overview' },
	{ id: 'units', label: 'Storage Units' },
	{ id: 'information', label: 'Information' },
	{ id: 'reviews', label: 'Reviews' },
];
const STICKY_NAV_SECTION_IDS = STICKY_NAV_SECTIONS.map(({ id }) => id);
// Sections whose hash gets discarded on load rather than honored - i.e. only
// `overview`. StackedUnits (the "Compare N units" card on search results)
// links straight to `${facilityURL}#units`, a live inbound deep link this
// app still produces, so `units` is exempt. `reviews` and `information` are
// exempt too: a #reviews/#information URL shared in an email, chat, or a
// Google jump-to-section link is a real inbound deep link, not a stale one,
// even though jump-nav tab clicks no longer write those hashes themselves.
const DISCARDABLE_HASH_SECTION_IDS = ['overview'];

// Single source of truth for the sticky reserve button's label, passed to
// StickyReserveBar as reserveLabel below so the rendered text and the
// click_text sent to trackCTAClick can't drift apart - StickyReserveBar's
// own default is only a fallback for callers that don't pass this prop.
const STICKY_RESERVE_LABEL = 'Reserve for Free';

// Where a jump-nav tab click actually lands, per section.
function scrollToSection(sectionId) {
	if (sectionId === 'overview') {
		// Overview sits directly below the site header, so jumping to
		// #overview itself scrolls the header out of view. The header
		// isn't fixed/sticky, so scrolling to the very top of the page
		// keeps it visible too - simpler and more robust than targeting
		// the header's own class name, which lives in a separate, shared
		// component and could change independently of this page.
		scrollTo(0, 'smooth');
		return;
	}

	scrollToElement(`#${sectionId}`, {
		// Information lands flush against the header without a little
		// breathing room above it; the other sections don't need this.
		...(sectionId === 'information' && { offset: -50 }),
		behavior: 'smooth',
	});
}

function computeDiscounts(itemsObj, perRow = 3) {
	if (!itemsObj) return { discountItems: [], rows: [] };

	const discountItems = Object.entries(itemsObj)
		.filter(([, value]) => value?.hasAttribute === true)
		.map(([key, value]) => ({ key, label: value.label }));

	const rows = [];
	for (let i = 0; i < discountItems.length; i += perRow) {
		rows.push(discountItems.slice(i, i + perRow));
	}

	return { discountItems, rows };
}

const EMPTY = {};

export function FacilityPageV2({
	facility,
	phone,
	reviews,
	reviewPageNum,
	reviewSource,
	baseUrl,
	storageUnits,
	vehicleUnits,
	loading,
	trackingPixel,
	meta,
	isNewSearchPage,
	isGoogleReviewEnabled,
	nearbyFacilities,
	googleReviews,
	scarcityShow,
	statusCode: statusCodeProp,
	match,
	showStickyReserveExperience,
}) {
	const dispatch = useDispatch();
	const ownProps = useMemo(() => ({ match }), [match]);
	const selectorResult =
		useSelector((state) => facilitySelector(state, ownProps)) || EMPTY;
	const {
		reviews: storeReviews,
		reviewPageNum: storeReviewPageNum,
		reviewSource: storeReviewSource,
		baseUrl: storeBaseUrl,
		storageUnits: storeStorageUnits,
		vehicleUnits: storeVehicleUnits,
		loading: storeLoading,
		trackingPixel: storeTrackingPixel,
		meta: storeMeta,
		scarcityShow: storeScarcityShow,
		isNewSearchPage: storeIsNewSearchPage,
		isGoogleReviewEnabled: storeIsGoogleReviewEnabled,
		nearbyFacilities: storeNearbyFacilities,
		googleReviews: storeGoogleReviews,
		facility: storeFacility,
		phone: storePhone,
	} = selectorResult || {};

	const resolvedFacility = facility ?? storeFacility;
	const resolvedPhone = phone ?? storePhone;
	const resolvedReviews = reviews ?? storeReviews;
	const resolvedReviewPageNum = reviewPageNum ?? storeReviewPageNum;
	const resolvedReviewSource = reviewSource ?? storeReviewSource;
	const resolvedBaseUrl = baseUrl ?? storeBaseUrl;
	const resolvedStorageUnits = storageUnits ?? storeStorageUnits ?? [];
	const resolvedVehicleUnits = vehicleUnits ?? storeVehicleUnits ?? [];
	const resolvedLoading = loading ?? storeLoading;
	const resolvedTrackingPixel = trackingPixel ?? storeTrackingPixel;
	const resolvedMeta = meta ?? storeMeta ?? {};
	const resolvedScarcityShow = scarcityShow ?? storeScarcityShow;
	const resolvedIsNewSearchPage = isNewSearchPage ?? storeIsNewSearchPage;
	const resolvedIsGoogleReviewEnabled =
		isGoogleReviewEnabled ?? storeIsGoogleReviewEnabled;
	const resolvedNearbyFacilities = nearbyFacilities ?? storeNearbyFacilities;
	const resolvedGoogleReviews = googleReviews ?? storeGoogleReviews;
	const statusCode = statusCodeProp ?? selectorResult?.statusCode;

	// Replaces withShowHide('description'), withShowHide('amenities'), withShowHide('facilityPhone') HOCs
	const [descriptionShowing, setDescriptionShowing] = useState(false);
	const [amenitiesShowing, setAmenitiesShowing] = useState(false);
	const [facilityPhoneShowing, setFacilityPhoneShowing] = useState(false);
	const onToggleDescription = useCallback(
		() => setDescriptionShowing((s) => !s),
		[],
	);
	const onToggleAmenities = useCallback(
		() => setAmenitiesShowing((s) => !s),
		[],
	);
	const onShowFacilityPhone = useCallback(
		() => setFacilityPhoneShowing(true),
		[],
	);

	// Replaces withSearchHandler HOC — provides handleSearch for OGFFacilityPage
	const handleSearch = useSearchHandler();

	// Replaces withPageViewTracking HOC
	usePageViewTracking(PAGE_TYPES.FACILITY);

	const actions = useMemo(
		() => ({
			changeFormData: (...args) => dispatch(changeFormData(...args)),
			createBooking: (...args) => dispatch(createBooking(...args)),
			onCreateReview: (...args) => dispatch(onCreateReview(...args)),
			getFacilityPhoneNumber: (...args) =>
				dispatch(getFacilityCardPhoneNumber(...args)),
		}),
		[dispatch],
	);

	const scrollToReviews = useCallback(() => scrollToElement('#reviews'), []);

	// SF-22689: mobile sticky Reserve Now bar + jump nav (Optimizely A/B
	// test). CSS hides the bar above the `large` breakpoint (767px); this JS
	// gate additionally skips creating the IntersectionObservers on desktop,
	// using the same breakpoint convention as SearchMap/NewFacilityCard.
	const { viewportWidth } = useViewportWidth();
	const isStickyReserveEnabled =
		showStickyReserveExperience && !isLarge(viewportWidth);

	// The section elements only exist once the in-network facility page below
	// has actually rendered (not the <LoadingPage /> or OGFFacilityPage
	// branches) - `ready` lets the hook retry observing them once that
	// happens, since `isStickyReserveEnabled` above is already true on the
	// very first render and won't change to re-trigger it otherwise.
	const sectionsReady = Boolean(
		resolvedFacility?.location?.address && resolvedFacility?.inNetwork,
	);

	const [activeSection, notifyActiveSectionClick] = useActiveSection(
		STICKY_NAV_SECTION_IDS,
		{
			enabled: isStickyReserveEnabled,
			ready: sectionsReady,
		},
	);

	// A hard refresh should always land on Overview. Two distinct cases
	// both need this: (1) a fresh load of a URL carrying a stale #overview
	// hash (redundant with the no-hash case, but still worth normalizing
	// away) - the browser's own native anchor-scroll has already jumped to
	// the hashed section by the time this effect runs. (2) no hash at all,
	// but a hard refresh after tapping a jump-nav tab - the tabs are plain
	// buttons that never touch the URL (see StickyJumpNav), so there's no
	// hash to warn us, yet history.scrollRestoration defaults to 'auto' and
	// the browser restores the mid-page scroll offset it saw right before
	// the reload. Either way, pull back to the top instantly (not 'smooth',
	// to avoid a jarring jump-then-scroll-back); useActiveSection's
	// scroll-top pin then takes over to mark Overview active once the
	// scroll event fires.
	//
	// (#units, #reviews, and #information are all exempt - see
	// DISCARDABLE_HASH_SECTION_IDS - and so is any other real hash on the
	// page, e.g. #leave-a-review: only reset when there's no hash to
	// honor, or the hash is the one we know is redundant with no-hash.)
	//
	// viewportWidth starts at 0 (see useViewportWidth), so
	// isStickyReserveEnabled reads true on every device's very first
	// render, desktop included, until the real width is measured. Wait
	// for that real measurement before latching, or a desktop visitor
	// opening a shared #overview link gets yanked to the top before the
	// mobile-only sticky bar's own gating has a chance to exclude them.
	//
	// sectionsReady and resolvedFacility.isVisible mirror the conditions
	// the rest of the feature is gated on (useActiveSection's `ready` and
	// the StickyReserveBar's own render check below) - without them, a
	// non-bookable facility page or the LoadingPage/OGFFacilityPage
	// branches would latch and reset the scroll even though none of the
	// jump-nav sections or the bar itself exist there.
	const hasResetInitialScrollRef = useRef(false);
	// The scrollTo call below alone can lose to the browser's own scroll
	// restoration: Chrome/Safari re-apply the recorded offset
	// asynchronously as the page's height grows (lazy images, units list,
	// reviews), so a restoration landing after this effect re-scrolls the
	// user back down. Opting out via scrollRestoration = 'manual' stops
	// any further native restoration from firing during that window.
	//
	// scrollRestoration is a property of the *current session history
	// entry*, not a transient document-level flag - per spec it stays
	// recorded on this facility page's own entry until explicitly changed
	// again. Since this isn't a SPA route (see isRestoredTraversal below),
	// navigating to a unit or nearby facility is a full document
	// navigation, so the React unmount cleanup never runs; leaving
	// 'manual' set would make the browser decline to restore scroll when
	// the user hits Back into this same entry, defeating that guard. Flip
	// it back to the previous value once the risky async-restoration
	// window has passed (the 'load' event, or immediately if already
	// loaded), with 'beforeunload' and unmount as fallbacks for a
	// navigation away before 'load' fires.
	const previousScrollRestorationRef = useRef(null);
	const restoreScrollRestorationRef = useRef(() => {
		if (previousScrollRestorationRef.current !== null) {
			globalThis.history.scrollRestoration =
				previousScrollRestorationRef.current;
			previousScrollRestorationRef.current = null;
		}
	});
	// Once 'load' fires, restore() has already run and the 'beforeunload'
	// listener can never do anything useful again - but leaving it
	// attached still disqualifies the page from Firefox's bfcache for the
	// rest of its life, since unmount (where it'd otherwise get removed)
	// never runs on this page's normal full-document navigations. Have
	// the 'load' handler remove it as soon as it's dead weight.
	const onLoadRef = useRef(() => {
		globalThis.removeEventListener(
			'beforeunload',
			restoreScrollRestorationRef.current,
		);
		restoreScrollRestorationRef.current();
	});
	// Cleanup lives in its own unmount-only effect below. viewportWidth
	// (via useViewportWidth's undebounced resize listener) can change
	// while the pre-load window below is still open; a cleanup tied to
	// this effect's deps would then run on every such change, not just
	// unmount - calling restore() early and flipping scrollRestoration
	// back to 'auto' mid-race. Since hasResetInitialScrollRef.current is
	// already latched at that point, the effect's next run would just
	// return without re-arming the guard, leaving it permanently off.
	useEffect(() => {
		if (
			!isStickyReserveEnabled ||
			!viewportWidth ||
			!sectionsReady ||
			!resolvedFacility?.isVisible ||
			hasResetInitialScrollRef.current
		) {
			return;
		}
		hasResetInitialScrollRef.current = true;

		const currentHash = globalThis.location.hash.slice(1);
		// A hash present and NOT in DISCARDABLE_HASH_SECTION_IDS - e.g.
		// #leave-a-review or #units - is a real deep link; leave it alone.
		if (
			currentHash &&
			!DISCARDABLE_HASH_SECTION_IDS.includes(currentHash)
		) {
			return;
		}

		// The no-hash case only means "hard refresh" for a fresh document
		// load. A back/forward traversal remounts this page too (it's not
		// a SPA route) but the user has an actively-scrolled position they
		// expect back - e.g. Back from a unit's detail page, a
		// nearby-facility card, or the review flow. Only the stale-hash
		// case above should discard that regardless of traversal type.
		const isRestoredTraversal =
			globalThis.performance?.getEntriesByType?.('navigation')[0]
				?.type === 'back_forward';
		if (!currentHash && isRestoredTraversal) {
			return;
		}

		previousScrollRestorationRef.current =
			globalThis.history.scrollRestoration;
		globalThis.history.scrollRestoration = 'manual';
		scrollTo(0, 'instant');
		if (currentHash) {
			const { pathname, search } = globalThis.location;
			globalThis.history.replaceState(null, '', `${pathname}${search}`);
		}

		if (globalThis.document.readyState === 'complete') {
			restoreScrollRestorationRef.current();
		} else {
			globalThis.addEventListener(
				'beforeunload',
				restoreScrollRestorationRef.current,
			);
			globalThis.addEventListener('load', onLoadRef.current, {
				once: true,
			});
		}
	}, [
		isStickyReserveEnabled,
		viewportWidth,
		sectionsReady,
		resolvedFacility?.isVisible,
	]);
	useEffect(() => {
		const restore = restoreScrollRestorationRef.current;
		const onLoad = onLoadRef.current;
		return () => {
			globalThis.removeEventListener('beforeunload', restore);
			globalThis.removeEventListener('load', onLoad);
			restore();
		};
	}, []);

	const handleStickyTabClick = useCallback(
		(sectionId, event) => {
			event?.preventDefault?.();

			if (sectionId === activeSection) return;

			const sectionLabel =
				STICKY_NAV_SECTIONS.find((section) => section.id === sectionId)
					?.label ?? sectionId;
			trackCTAClick(
				`${GTM_IDS.STICKY_JUMP_NAV_LINK_ID_PREFIX}${sectionId}`,
				'Jump Nav Tab',
				sectionLabel,
				`#${sectionId}`,
			);
			// A short target section (e.g. a collapsed accordion) can end up
			// entirely above the IntersectionObserver's activation line once
			// the scroll below settles, which would otherwise report the next
			// section as active instead of the one just clicked.
			notifyActiveSectionClick(sectionId);
			scrollToSection(sectionId);
		},
		[activeSection, notifyActiveSectionClick],
	);

	// The sticky Reserve Now button only ever scrolls to Storage Units (never
	// redirects, so it can't start a duplicate/premature reservation for
	// whichever unit happens to be on screen) - and does nothing at all if
	// Storage Units is already the active section.
	const handleStickyReserveClick = useCallback(() => {
		if (activeSection === 'units') return;

		trackCTAClick(
			GTM_IDS.STICKY_RESERVE_BUTTON,
			'Sticky Reserve Button',
			STICKY_RESERVE_LABEL,
			'#units',
		);
		scrollToElement('#units', {
			behavior: 'smooth',
		});
	}, [activeSection]);

	const [reviewFormShowing, setReviewFormShowing] = useState(false);
	useEffect(() => {
		if (!resolvedFacility?.inNetwork) return;
		TrackingClient.trackFacilityViewed({
			facility_id: resolvedFacility.id,
			facility_name: resolvedFacility.name,
			facility_promo: resolvedFacility.promo,
			facility_rank: resolvedFacility.rank,
		});
		// Fire once per viewed facility, keyed by id - not on every render
		// where name/promo/rank happen to change identity (e.g. promo data
		// arriving after the initial render), which would double-track a
		// single view.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resolvedFacility?.id]);

	const { noIndex } = resolvedMeta;
	const { discountItems: availableDiscounts, rows: discountRows } =
		useMemo(() => {
			const items =
				resolvedFacility?.groupedAmenities?.discounts?.items || null;
			return computeDiscounts(items, 3);
		}, [resolvedFacility?.groupedAmenities?.discounts?.items]);

	const hasGoogleReviews =
		resolvedIsGoogleReviewEnabled &&
		resolvedGoogleReviews?.reviews &&
		Array.isArray(resolvedGoogleReviews.reviews) &&
		resolvedGoogleReviews.reviews.length > 0;
	const hasVerifiedReviews =
		Array.isArray(resolvedReviews) && resolvedReviews.length > 0;

	const renderReviewsSection = () => {
		if (hasGoogleReviews && hasVerifiedReviews) {
			return (
				<ReviewTabs
					verifiedCount={resolvedReviews.length}
					googleCount={resolvedGoogleReviews.user_ratings_total}
					verifiedContent={
						<FacilityReviews
							reviews={resolvedReviews}
							facility={resolvedFacility}
							reviewPageNum={resolvedReviewPageNum}
							baseUrl={resolvedBaseUrl}
							source={resolvedReviewSource}
							onCreateReview={actions.onCreateReview}
						/>
					}
					googleContent={renderGoogleReviewCardList()}
				/>
			);
		}
		if (hasGoogleReviews) {
			return (
				<>
					<Expand showing={reviewFormShowing}>
						<div>
							<ReviewForm
								onSubmit={actions.onCreateReview}
								source={resolvedReviewSource}
								facilityId={resolvedFacility.id}
							/>
						</div>
					</Expand>
					{renderGoogleReviewCardList()}
				</>
			);
		}
		return (
			<FacilityReviews
				reviews={resolvedReviews}
				facility={resolvedFacility}
				reviewPageNum={resolvedReviewPageNum}
				baseUrl={resolvedBaseUrl}
				source={resolvedReviewSource}
				onCreateReview={actions.onCreateReview}
			/>
		);
	};

	const renderGoogleReviewCardList = () => (
		<div className="google-review-card-list">
			{resolvedGoogleReviews.reviews.map((review) => (
				<GoogleReviewCard
					key={review.author_name + review.time}
					reviewerName={review.author_name}
					reviewerPhoto={review.profile_photo_url}
					rating={review.rating}
					reviewDate={review.time}
					reviewText={review.text}
				/>
			))}
			{resolvedGoogleReviews.placeId && (
				<div className="google-review-cta">
					<a
						href={`https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${resolvedGoogleReviews.placeId}`}
						target="_blank"
						rel="noopener noreferrer"
						className="google-review-cta__button"
					>
						View all reviews on Google
					</a>
				</div>
			)}
		</div>
	);

	// Replaces withErrorHandler HOC
	if (statusCode > 200) {
		return <ErrorPage statusCode={statusCode} />;
	}

	// Replaces withRouteHandler((props) => !props.facility?.location?.address)
	if (!resolvedFacility?.location?.address) {
		return <LoadingPage />;
	}

	if (!resolvedFacility?.inNetwork) {
		return (
			<OGFFacilityPage
				facility={resolvedFacility}
				phone={resolvedPhone}
				reviews={resolvedReviews}
				reviewPageNum={resolvedReviewPageNum}
				reviewSource={resolvedReviewSource}
				baseUrl={resolvedBaseUrl}
				storageUnits={resolvedStorageUnits}
				vehicleUnits={resolvedVehicleUnits}
				loading={resolvedLoading}
				trackingPixel={resolvedTrackingPixel}
				meta={resolvedMeta}
				scarcityShow={resolvedScarcityShow}
				isNewSearchPage={resolvedIsNewSearchPage}
				isGoogleReviewEnabled={resolvedIsGoogleReviewEnabled}
				scrollToReviews={scrollToReviews}
				nearbyFacilities={resolvedNearbyFacilities}
				googleReviews={resolvedGoogleReviews}
				handleSearch={handleSearch}
				facilityPhoneShowing={facilityPhoneShowing}
				onShowFacilityPhone={onShowFacilityPhone}
				amenitiesShowing={amenitiesShowing}
				descriptionShowing={descriptionShowing}
				onToggleDescription={onToggleDescription}
				onToggleAmenities={onToggleAmenities}
				actions={actions}
			/>
		);
	}
	return (
		<Page
			className="facility-page-v2"
			trackingPixel={resolvedTrackingPixel}
			pageType={PAGE_TYPES.FACILITY}
			title={resolvedMeta.title}
			description={resolvedMeta.description}
			canonical={resolvedMeta.canonical}
			noIndex={noIndex}
			noFollow={noIndex}
		>
			<SelfStorageSchema
				facility={resolvedFacility}
				phone={resolvedPhone}
				baseUrl={resolvedBaseUrl}
				units={{ storageUnits: resolvedStorageUnits }}
				googleReviews={resolvedGoogleReviews}
			/>
			<WebPageSchema
				facility={resolvedFacility}
				baseUrl={resolvedBaseUrl}
			/>
			<WebSiteSchema />
			<UnitProductSchema
				units={[...resolvedStorageUnits, ...resolvedVehicleUnits]}
				facility={resolvedFacility}
				baseUrl={resolvedBaseUrl}
			/>
			<Section
				id="overview"
				className="facility-header-section"
			>
				<Container className="facility-header">
					<FacilityBreadcrumbs
						facility={resolvedFacility}
						baseUrl={resolvedBaseUrl}
					/>
				</Container>
				{!resolvedFacility.isVisible ? (
					<Container className="facility-banner-visible">
						<TopBanner
							className="top-banner__promo"
							content="Online reservations aren't available through SelfStorage. Please contact the facility directly to book."
						/>
					</Container>
				) : (
					<Container>
						<TopBanner
							className="top-banner__promo"
							title="Reserve Now!"
							content="Pricing and availability may change."
						/>
					</Container>
				)}
				<FacilityDetails
					facility={resolvedFacility}
					reviews={resolvedReviews}
					numberOfRatings={resolvedFacility.reviews?.number}
					phone={resolvedFacility.isVisible ? resolvedPhone : null}
					facilityUrl={resolvedFacility.url}
					googleReviews={resolvedGoogleReviews}
					isGoogleReviewEnabled={resolvedIsGoogleReviewEnabled}
					scrollToReviews={scrollToReviews}
					showSchema={false}
				/>
			</Section>
			<FacilityGallery
				facility={resolvedFacility}
				facilities={resolvedNearbyFacilities}
				baseUrl={resolvedBaseUrl}
			/>
			<Container>
				<Section className="facility-amenities-ranked">
					<Section className="section-heading">
						<H2
							size="large"
							weight="extraBold"
							last
						>
							Key Features
						</H2>
					</Section>
					<FacilityAmenitiesRanked
						groupedAmenities={resolvedFacility.groupedAmenities}
						storageUnits={resolvedStorageUnits}
						vehicleUnits={resolvedVehicleUnits}
					/>
				</Section>
			</Container>
			{resolvedFacility.isVisible && (
				<>
					<Section
						id="units"
						className="facility-units-section"
					>
						<Container>
							<Section>
								{(resolvedStorageUnits.length > 0 ||
									resolvedVehicleUnits.length > 0) &&
									(availableDiscounts?.length > 0 ||
										resolvedFacility?.hasOnlineMoveins) && (
										<section className="discount-banners">
											{resolvedFacility?.hasOnlineMoveins && (
												<section className="content-container">
													<div className="discount-row discount-row--1">
														<TopBanner
															className="top-banner_omi"
															iconAlt="Online Move In Icon"
															icon={OMIIcon}
															title="Online Move-In Available"
															content="Rent online, skip the counter, and go straight to your space"
														/>
													</div>
												</section>
											)}

											{availableDiscounts.length > 0 && (
												<section className="content-container">
													{discountRows.map((row) => (
														<div
															className={`discount-row discount-row--${row.length}`}
															key={row
																.map(
																	(d) =>
																		d.key,
																)
																.join('-')}
														>
															{row.map(
																({
																	key,
																	label,
																}) => (
																	<div
																		className="discount-item"
																		key={
																			key
																		}
																	>
																		<TopBanner
																			className="top-banner_discount"
																			title={label
																				.split(
																					'(',
																				)[0]
																				.trim()}
																			icon={
																				Coupon
																			}
																			iconAlt="Discount Icon"
																			tooltipIcon={
																				questionMarkIcon
																			}
																			tooltipMessage={
																				label
																			}
																		/>
																	</div>
																),
															)}
														</div>
													))}
												</section>
											)}
										</section>
									)}
							</Section>
							<FacilityUnitListV2
								facility={resolvedFacility}
								storageUnits={[
									...resolvedStorageUnits,
									...resolvedVehicleUnits,
								]}
								vehicleUnits={resolvedVehicleUnits}
								changeFormData={actions.changeFormData}
								onCreateBooking={actions.createBooking}
								isNewSearchPage={resolvedIsNewSearchPage}
								loading={resolvedLoading}
							/>
						</Container>
					</Section>
					<Section id="information">
						<Container>
							<FeaturesAmenitiesAccordion
								facility={resolvedFacility}
								title="Facility Overview & What to Expect"
							/>
						</Container>
					</Section>
					<Section
						id="reviews"
						className="facility-units-section"
					>
						<Container>
							<div className="reviews-heading-wrapper">
								<H2
									size="large"
									weight="extraBold"
									className="reviews-heading"
								>
									Verified Customer Reviews
								</H2>
								{hasGoogleReviews && !hasVerifiedReviews && (
									<button
										type="button"
										className="leave-review-link"
										onClick={() =>
											setReviewFormShowing(
												(prev) => !prev,
											)
										}
									>
										Leave a review of this facility
									</button>
								)}
							</div>
							{renderReviewsSection()}
						</Container>
					</Section>
					<HowItWorks />
				</>
			)}
			<Section className="facility-nearby-facilities">
				<StorageNearYou type="Facility" />
			</Section>
			{isStickyReserveEnabled && resolvedFacility.isVisible && (
				<StickyReserveBar
					sections={STICKY_NAV_SECTIONS}
					activeSection={activeSection}
					onTabClick={handleStickyTabClick}
					onReserveClick={handleStickyReserveClick}
					reserveLabel={STICKY_RESERVE_LABEL}
				/>
			)}
		</Page>
	);
}

FacilityPageV2.propTypes = {
	baseUrl: PropTypes.string,
	facility: PropTypes.object,
	googleReviews: PropTypes.object,
	isGoogleReviewEnabled: PropTypes.bool,
	isNewSearchPage: PropTypes.bool,
	loading: PropTypes.bool,
	match: PropTypes.object,
	meta: PropTypes.shape(metaPropTypes),
	nearbyFacilities: PropTypes.arrayOf(PropTypes.object),
	phone: phonePropType,
	reviewPageNum: PropTypes.number,
	reviews: PropTypes.arrayOf(PropTypes.object),
	reviewSource: PropTypes.string,
	scarcityShow: PropTypes.bool,
	showStickyReserveExperience: PropTypes.bool,
	statusCode: PropTypes.number,
	storageUnits: PropTypes.arrayOf(PropTypes.object),
	trackingPixel: PropTypes.string,
	vehicleUnits: PropTypes.arrayOf(PropTypes.object),
};

export default FacilityPageV2;
